import React, { useState, useEffect } from 'react';
import { User } from '../types';
import NavigationSidebar from '../components/NavigationSidebar';

interface HelpScreenProps {
  currentUser: User;
  onBack: () => void;
  onGoToContact: () => void;
  onGoToMenu?: () => void;
  onGoToPrivacySecurity?: () => void;
  onGoToSecurityPolicy?: () => void;
  onGoToCommunityGuidelines?: () => void;
  onGoToTermsOfService?: () => void;
  onGoToAbout?: () => void;
}

const HelpScreen: React.FC<HelpScreenProps> = ({ 
  currentUser, 
  onBack, 
  onGoToContact,
  onGoToMenu,
  onGoToPrivacySecurity,
  onGoToSecurityPolicy,
  onGoToCommunityGuidelines,
  onGoToTermsOfService,
  onGoToAbout,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div style={{
      height: '100vh',
      background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 50%, #dcdcdc 100%)',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'Arial, sans-serif',
      color: '#333',
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* サイドバー（スマホのみ） */}
      {isMobile && (
        <NavigationSidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(!sidebarOpen)}
          currentPage="help"
          onGoToMenu={onGoToMenu || onBack}
          onGoToHelp={onBack}
          onGoToPrivacySecurity={onGoToPrivacySecurity || onBack}
          onGoToSecurityPolicy={onGoToSecurityPolicy || onBack}
          onGoToCommunityGuidelines={onGoToCommunityGuidelines || onBack}
          onGoToTermsOfService={onGoToTermsOfService || onBack}
          onGoToAbout={onGoToAbout || onBack}
        />
      )}

      {/* ヘッダー */}
      <div style={{
        padding: '15px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
        flexShrink: 0,
        background: 'rgba(255, 255, 255, 0.9)'
      }}>
        {isMobile ? (
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '5px',
              color: '#333'
            }}
          >
            ☰
          </button>
        ) : (
          <h1 style={{ margin: 0, fontSize: '24px' }}>ヘルプ</h1>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {isMobile && (
            <button
              onClick={onGoToMenu || onBack}
              style={{
                background: '#667eea',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              メニューに戻る
            </button>
          )}
          {!isMobile && (
            <button
              onClick={onBack}
              style={{
                background: '#667eea',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              ← 戻る
            </button>
          )}
        </div>
      </div>

      {/* メインコンテンツ */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        padding: '20px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        minHeight: 0
      }}>
        <div style={{
          maxWidth: '800px',
          width: '100%',
          overflow: 'auto'
        }}>
          {/* 検索バー */}
          <div style={{
            marginBottom: '40px'
          }}>
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              background: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '10px',
              padding: '12px 20px',
              border: '1px solid rgba(0, 0, 0, 0.15)'
            }}>
              <span style={{ fontSize: '20px', marginRight: '10px' }}>🔍</span>
              <input
                type="text"
                placeholder="Q ヘルプを検索"
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  color: '#333',
                  fontSize: '16px',
                  outline: 'none'
                }}
              />
            </div>
          </div>

          {/* よくあるご質問 */}
          <div style={{
            marginBottom: '40px'
          }}>
            <h2 style={{
              fontSize: '20px',
              marginBottom: '20px',
              borderBottom: '2px solid rgba(0, 0, 0, 0.2)',
              paddingBottom: '10px'
            }}>
              よくあるご質問
            </h2>
            <div style={{
              background: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '12px',
              padding: '20px'
            }}>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                <li style={{
                  padding: '10px 0',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
                }}>
                  <span style={{ marginRight: '10px' }}>•</span>
                  ログインできない
                </li>
                <li style={{
                  padding: '10px 0',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
                }}>
                  <span style={{ marginRight: '10px' }}>•</span>
                  通話が接続できない
                </li>
                <li style={{
                  padding: '10px 0',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
                }}>
                  <span style={{ marginRight: '10px' }}>•</span>
                  マイクの音声が聞こえない
                </li>
                <li style={{
                  padding: '10px 0',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
                }}>
                  <span style={{ marginRight: '10px' }}>•</span>
                  プロフィールの変更方法
                </li>
                <li style={{
                  padding: '10px 0'
                }}>
                  <span style={{ marginRight: '10px' }}>•</span>
                  アカウントの削除方法
                </li>
              </ul>
            </div>
          </div>

          {/* ご質問カテゴリ */}
          <div style={{
            marginBottom: '40px'
          }}>
            <h2 style={{
              fontSize: '20px',
              marginBottom: '20px',
              borderBottom: '2px solid rgba(0, 0, 0, 0.2)',
              paddingBottom: '10px'
            }}>
              ご質問カテゴリ
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '20px'
            }}>
              {/* カテゴリ1: 登録情報・ログイン */}
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '12px',
                  padding: '20px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ fontSize: '32px', marginBottom: '10px' }}>👤</div>
                <h3 style={{
                  fontSize: '16px',
                  margin: '0 0 8px 0',
                  fontWeight: 'bold'
                }}>
                  登録情報・ログイン
                </h3>
                <p style={{
                  fontSize: '12px',
                  margin: 0,
                  color: 'rgba(255, 255, 255, 0.8)',
                  lineHeight: '1.5'
                }}>
                  ログインできない / パスワードを忘れた / メールアドレスの変更
                </p>
              </div>

              {/* カテゴリ2: 通話機能 */}
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '12px',
                  padding: '20px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ fontSize: '32px', marginBottom: '10px' }}>📞</div>
                <h3 style={{
                  fontSize: '16px',
                  margin: '0 0 8px 0',
                  fontWeight: 'bold'
                }}>
                  通話機能
                </h3>
                <p style={{
                  fontSize: '12px',
                  margin: 0,
                  color: 'rgba(255, 255, 255, 0.8)',
                  lineHeight: '1.5'
                }}>
                  通話が接続できない / マイクの音声が聞こえない / マッチングについて
                </p>
              </div>

              {/* カテゴリ3: 基本機能 */}
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '12px',
                  padding: '20px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ fontSize: '32px', marginBottom: '10px' }}>⚙️</div>
                <h3 style={{
                  fontSize: '16px',
                  margin: '0 0 8px 0',
                  fontWeight: 'bold'
                }}>
                  基本機能
                </h3>
                <p style={{
                  fontSize: '12px',
                  margin: 0,
                  color: 'rgba(255, 255, 255, 0.8)',
                  lineHeight: '1.5'
                }}>
                  プロフィール設定 / お気に入り / 通話履歴
                </p>
              </div>

              {/* カテゴリ4: お問い合わせ */}
              <div
                onClick={onGoToContact}
                style={{
                  background: 'rgba(102, 126, 234, 0.3)',
                  borderRadius: '12px',
                  padding: '20px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: '2px solid rgba(102, 126, 234, 0.5)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(102, 126, 234, 0.4)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(102, 126, 234, 0.3)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ fontSize: '32px', marginBottom: '10px' }}>✉️</div>
                <h3 style={{
                  fontSize: '16px',
                  margin: '0 0 8px 0',
                  fontWeight: 'bold'
                }}>
                  お問い合わせ
                </h3>
                <p style={{
                  fontSize: '12px',
                  margin: 0,
                  color: 'rgba(255, 255, 255, 0.9)',
                  lineHeight: '1.5'
                }}>
                  サービスに関するお問い合わせはこちら
                </p>
                <div style={{
                  marginTop: '10px',
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontWeight: 'bold'
                }}>
                  → お問い合わせフォーム
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* フッター */}
      <div style={{
        padding: '10px 20px',
        textAlign: 'center',
        fontSize: '12px',
        color: 'rgba(255, 255, 255, 0.7)',
        borderTop: '1px solid rgba(255, 255, 255, 0.2)',
        flexShrink: 0
      }}>
        Voitter © 2025
      </div>
    </div>
  );
};

export default HelpScreen;

